import type { Rating, SeasonRating } from "../types/rating";
import { isUnknownArray, isRecord } from "../types/data";
export type RatedSeason = SeasonRating & { key: string; userRate: number };
type StatisticsItem = Rating & { contentType?: string };
const MOVIE_TYPE = "movie";
const SERIES_TYPE = "tv";
const SEASON_TYPE = "season";
const EPISODE_TYPE = "episode";

export const MEDIA_STATISTICS_FILTERS = {
  ALL: "all",
  MOVIES: MOVIE_TYPE,
  SERIES: SERIES_TYPE
};

const asArray = <T>(value: T | T[] | null | undefined): T[] => isUnknownArray(value) ? value : value == null ? [] : [value];

const toPositiveNumber = (value: unknown): number | null => {
  if (isUnknownArray(value)) {
    for (const item of value) {
      const number = toPositiveNumber(item);
      if (number != null) return number;
    }
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const toRatingNumber = (value: unknown): number | null => {
  if (value == null || String(value).trim() === "") return null;
  const number = Number(String(value).replace(",", "."));
  return Number.isFinite(number) && number >= 0 && number <= 10
    ? Math.round(number * 100) / 100
    : null;
};

const average = (values: unknown[]): number | null => {
  const numeric = values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  return numeric.length
    ? numeric.reduce((sum, value) => sum + value, 0) / numeric.length
    : null;
};

const normalizeMediaType = (value: unknown) =>
  String(value || "").trim().toLocaleLowerCase();

export const getStatisticsMediaType = (item: StatisticsItem = {}) => {
  const source = item || {};
  const rawType = normalizeMediaType(
    source.mediaType || source.media_type || source.contentType || source.type
  );

  if (rawType === SEASON_TYPE || rawType.includes("сезон")) return SEASON_TYPE;
  if (rawType === EPISODE_TYPE || rawType.includes("эпизод")) return EPISODE_TYPE;
  if (
    rawType === SERIES_TYPE ||
    rawType === "series" ||
    rawType === "show" ||
    rawType.includes("сериал")
  ) return SERIES_TYPE;
  if (
    rawType === MOVIE_TYPE ||
    rawType === "film" ||
    rawType.includes("фильм")
  ) return MOVIE_TYPE;

  if (
    source.first_air_date ||
    source.firstAirDate ||
    isUnknownArray(source.seasons) ||
    source.seasonRatings
  ) return SERIES_TYPE;

  return MOVIE_TYPE;
};

export const isStatisticsMovie = (item: StatisticsItem) =>
  getStatisticsMediaType(item) === MOVIE_TYPE;

export const isStatisticsSeries = (item: StatisticsItem) =>
  getStatisticsMediaType(item) === SERIES_TYPE;

export const getStatisticsContentKey = (item: StatisticsItem = {}, index = 0) => {
  const source = item || {};
  const type = getStatisticsMediaType(source);
  const id = source.filmId ?? source.id ?? source.movieId ?? source.tmdbId;
  if (id != null && String(id).trim()) return `${type}:${String(id).trim()}`;

  const title = String(
    source.title || source.filmTitle || source.name || source.originalTitle || ""
  ).trim().toLocaleLowerCase();
  const release = String(
    source.releaseYear || source.releaseDate || source.release_date || source.first_air_date || ""
  ).slice(0, 10);
  return title ? `${type}:title:${title}:${release}` : `${type}:anonymous:${index}`;
};

export const getUniqueStatisticsRatings = <T extends Rating>(ratings: T[] = []): T[] => {
  const unique = new Map<string, T>();
  asArray(ratings).forEach((rating, index) => {
    if (!rating || rating.deletedAt || rating.active === false) return;
    const type = getStatisticsMediaType(rating);
    if (type !== MOVIE_TYPE && type !== SERIES_TYPE) return;
    unique.set(getStatisticsContentKey(rating, index), rating);
  });
  return [...unique.values()];
};

export const filterStatisticsRatings = (ratings: Rating[] = [], filter = "all") => {
  const unique = getUniqueStatisticsRatings(ratings);
  if (filter === MOVIE_TYPE || filter === SERIES_TYPE) {
    return unique.filter((rating: Rating) => getStatisticsMediaType(rating) === filter);
  }
  return unique;
};

export const getPrimaryStatisticsRating = (rating: Rating = {}) => {
  const source = rating || {};
  if (Number(source.ratingModelVersion) >= 2) {
    if (source.overallRatingExplicit !== true) return null;
    const direct = toRatingNumber(source.userRate ?? source.rating);
    if (direct === 0 && source.importSource === "ratings-table") return null;
    return direct;
  }
  const hasRatedSeasonScores = isStatisticsSeries(source) &&
    source.seasonRatings &&
    typeof source.seasonRatings === "object" &&
    Object.values(source.seasonRatings).some((season) => {
      return toRatingNumber(getNestedSeasonRating(season)) != null;
    });
  if (hasRatedSeasonScores) return null;

  const direct = toRatingNumber(source.userRate ?? source.rating);
  if (direct != null) {
    if (direct === 0 && source.importSource === "ratings-table") return null;
    return direct;
  }

  if (source.detailedRate && typeof source.detailedRate === "object") {
    const detailedAverage = average(
      Object.values(source.detailedRate).map(toRatingNumber).filter((value): value is number => value != null)
    );
    return detailedAverage == null ? null : Math.round(detailedAverage * 100) / 100;
  }

  // Оценки сезонов не становятся самостоятельными оценками сериала в
  // статистике. Сохранённая общая оценка сериала находится в userRate.
  return null;
};

export const getMovieRuntimeMinutes = (rating: Rating = {}) => {
  const source = rating || {};
  if (!isStatisticsMovie(source)) return null;
  return toPositiveNumber(
    source.runtime ??
    source.filmRuntime ??
    source.filmData?.runtime ??
    source.fullDataFilm?.runtime
  );
};

export const getEpisodeRuntimeMinutes = (rating: Rating = {}) => {
  const source = rating || {};
  if (!isStatisticsSeries(source)) return null;
  return toPositiveNumber(
    source.episode_run_time ??
    source.episodeRuntime ??
    source.filmData?.episode_run_time ??
    source.fullDataFilm?.episode_run_time ??
    source.runtime ??
    source.filmRuntime ??
    source.filmData?.runtime ??
    source.fullDataFilm?.runtime
  );
};

const getNestedSeasonRating = (season: { userRate?: unknown } = {}): unknown => {
  const value = season?.userRate;
  return isRecord(value) ? value.userRate : value;
};

export const getRatedSeasons = (rating: Rating = {}): RatedSeason[] => {
  if (!isStatisticsSeries(rating) || !rating.seasonRatings || typeof rating.seasonRatings !== "object") {
    return [];
  }

  return Object.entries(rating.seasonRatings).reduce<RatedSeason[]>((result, [key, season]) => {
    const seasonRating = toRatingNumber(getNestedSeasonRating(season));
    if (seasonRating == null) return result;
    result.push({
      ...(season && typeof season === "object" ? season : {}),
      key: String(key),
      userRate: seasonRating
    });
    return result;
  }, []);
};

// Episode ratings live in their own Firestore tree. A metadata-only series
// document must not turn into an empty card in the ordinary "All ratings"
// feed; that feed represents an overall series rating or a season rating.
export const hasRatingForAllRatings = (rating: Rating = {}) =>
  !isStatisticsSeries(rating) ||
  getPrimaryStatisticsRating(rating) != null ||
  getRatedSeasons(rating).length > 0;

const getExplicitWatchedEpisodeCount = (rating: Rating = {}) => {
  const watchedIds = rating.watchedEpisodeIds || rating.watchedEpisodesIds;
  if (isUnknownArray(watchedIds)) return watchedIds.length;
  if (isUnknownArray(rating.watchedEpisodes)) return rating.watchedEpisodes.length;
  if (rating.episodeRatings && typeof rating.episodeRatings === "object") {
    return Object.keys(rating.episodeRatings).length;
  }
  return toPositiveNumber(rating.watchedEpisodesCount ?? rating.watchedEpisodeCount);
};

const getSeasonEpisodeCount = (rating: Rating, ratedSeason: RatedSeason) => {
  const direct = toPositiveNumber(
    ratedSeason.episode_count ?? ratedSeason.episodeCount
  );
  if (direct != null) return direct;

  const keyCandidates = new Set([
    ratedSeason.key,
    ratedSeason.id,
    ratedSeason.season_number,
    ratedSeason.seasonNumber
  ].filter((value): value is number => value != null).map(String));
  const metadata = asArray(rating.seasons).find((season) =>
    [season?.id, season?.season_number, season?.seasonNumber]
      .filter((value): value is number => value != null)
      .map(String)
      .some((value) => keyCandidates.has(value))
  );
  return toPositiveNumber(metadata?.episode_count ?? metadata?.episodeCount);
};

export const getSeriesViewingStatistics = (rating: Rating = {}) => {
  if (!isStatisticsSeries(rating)) {
    return {
      ratedSeasons: 0,
      watchedEpisodes: null,
      episodeRuntime: null,
      minutes: null,
      hasExactEpisodeCount: false
    };
  }

  const episodeRuntime = getEpisodeRuntimeMinutes(rating);
  const ratedSeasons = getRatedSeasons(rating);
  const explicitEpisodeCount = getExplicitWatchedEpisodeCount(rating);
  const seasonEpisodeCounts = ratedSeasons.map((season) =>
    getSeasonEpisodeCount(rating, season)
  );
  const seasonCountsAreExact = ratedSeasons.length > 0 &&
    seasonEpisodeCounts.every((count) => count != null);
  const watchedEpisodes = explicitEpisodeCount != null
    ? explicitEpisodeCount
    : seasonCountsAreExact
      ? seasonEpisodeCounts.reduce<number>((sum, count) => sum + (count ?? 0), 0)
      : null;
  const minutes = episodeRuntime != null && watchedEpisodes != null
    ? episodeRuntime * watchedEpisodes
    : null;

  return {
    ratedSeasons: ratedSeasons.length,
    watchedEpisodes,
    episodeRuntime,
    minutes,
    hasExactEpisodeCount: watchedEpisodes != null
  };
};

export const buildMediaStatistics = (ratings: Rating[] = []) => {
  const all = getUniqueStatisticsRatings(ratings);
  const movies = all.filter(isStatisticsMovie);
  const series = all.filter(isStatisticsSeries);
  const movieRuntimes = movies.map(getMovieRuntimeMinutes).filter((value): value is number => value != null);
  const seriesViewing = series.map(getSeriesViewingStatistics);
  const knownSeriesViewing = seriesViewing.filter((item): item is typeof item & { minutes: number } => item.minutes != null);
  const movieMinutes = movieRuntimes.reduce((sum, value) => sum + value, 0);
  const seriesMinutes = knownSeriesViewing.reduce((sum, item) => sum + item.minutes, 0);
  const knownSeriesEpisodeCount = knownSeriesViewing.reduce(
    (sum, item) => sum + Number(item.watchedEpisodes || 0),
    0
  );
  const watchedEpisodes = seriesViewing
    .map((item) => item.watchedEpisodes)
    .filter((value): value is number => value != null);
  const totalWatchedEpisodes = watchedEpisodes.length
    ? watchedEpisodes.reduce((sum, value) => sum + value, 0)
    : null;
  const rates = all.map(getPrimaryStatisticsRating).filter((value): value is number => value != null);
  const movieRates = movies.map(getPrimaryStatisticsRating).filter((value): value is number => value != null);
  const seriesRates = series.map(getPrimaryStatisticsRating).filter((value): value is number => value != null);

  return {
    all,
    movies,
    series,
    totalCount: all.length,
    movieCount: movies.length,
    seriesCount: series.length,
    ratedSeasons: seriesViewing.reduce((sum, item) => sum + item.ratedSeasons, 0),
    watchedEpisodes: totalWatchedEpisodes,
    movieMinutes,
    seriesMinutes,
    totalMinutes: movieMinutes + seriesMinutes,
    averageMovieRuntime: average(movieRuntimes),
    averageEpisodeRuntime: knownSeriesEpisodeCount
      ? seriesMinutes / knownSeriesEpisodeCount
      : null,
    averageRating: average(rates),
    averageMovieRating: average(movieRates),
    averageSeriesRating: average(seriesRates),
    knownMovieRuntimeCount: movieRuntimes.length,
    knownSeriesRuntimeCount: knownSeriesViewing.length,
    seriesRuntimeComplete: series.length > 0 && knownSeriesViewing.length === series.length
  };
};
